import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wrench, MessageCircle, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useT } from '../i18n';
import { ToastMark, SquareMark, CloverMark, LightspeedMark } from './BrandIcons';

/* POS platforms shown in the animated marquee */
const POS_PLATFORMS = [
  { id: 'toast', name: 'Toast', domain: 'toasttab.com', Mark: ToastMark },
  { id: 'square', name: 'Square', domain: 'squareup.com', Mark: SquareMark },
  { id: 'clover', name: 'Clover', domain: 'clover.com', Mark: CloverMark },
  { id: 'lightspeed', name: 'Lightspeed', domain: 'lightspeedhq.com', Mark: LightspeedMark },
  { id: 'touchbistro', name: 'TouchBistro', domain: 'touchbistro.com' },
  { id: 'spoton', name: 'SpotOn', domain: 'spoton.com' },
  { id: 'revel', name: 'Revel Systems', domain: 'revelsystems.com' },
  { id: 'ncr', name: 'NCR Aloha', domain: 'ncr.com' },
  { id: 'micros', name: 'Oracle MICROS', domain: 'oracle.com' },
  { id: 'par', name: 'PAR Brink', domain: 'partech.com' },
  { id: 'skytab', name: 'SkyTab', domain: 'skytab.com' },
  { id: 'squirrel', name: 'Squirrel Systems', domain: 'squirrelsystems.com' },
];

function PosLogo({ p }) {
  const [err, setErr] = useState(false);
  if (err) {
    if (p.Mark) return <p.Mark className="w-8 h-8" />;
    return (
      <span className="w-8 h-8 rounded-lg bg-neutral-100 border border-neutral-200 flex items-center justify-center text-neutral-700 font-bold text-[13px]">
        {p.name.charAt(0)}
      </span>
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${p.domain}&sz=128`}
      alt={`${p.name} logo`}
      loading="lazy"
      decoding="async"
      onError={() => setErr(true)}
      className="w-8 h-8 rounded-lg object-contain bg-white ring-1 ring-neutral-100 p-0.5"
    />
  );
}

export default function ItPosExtras() {
  const { lang } = useT();
  const isEs = lang === 'es';

  return (
    <>
      {/* ---------------------------------------- POS platform marquee */}
      <section className="py-12 lg:py-16 bg-white border-y border-neutral-100" data-testid="pos-logo-marquee">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <p className="kx-eyebrow text-center mb-2">
            {isEs ? 'PLATAFORMAS POS QUE DOMINAMOS' : 'POS PLATFORMS WE MASTER'}
          </p>
          <p className="text-center text-neutral-600 text-sm sm:text-base max-w-2xl mx-auto">
            {isEs
              ? 'Sea cual sea tu sistema, lo conocemos por dentro. Y si aún no tienes uno, te ayudamos a elegir el correcto.'
              : 'Whatever system you run, we know it inside out. And if you don’t have one yet, we’ll help you choose the right one.'}
          </p>
        </div>
        <div className="mt-8 relative overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_8%,black_92%,transparent)]">
          <div className="flex w-max py-1 kx-marquee-slow">
            {[...POS_PLATFORMS, ...POS_PLATFORMS].map((p, i) => (
              <div
                key={`${p.id}-${i}`}
                className="mx-2.5 flex items-center gap-2.5 rounded-full border border-neutral-200 bg-white pl-2.5 pr-5 h-14 hover:border-[#F77F11] hover:shadow-[0_16px_44px_-20px_rgba(247,127,17,0.5)] hover:-translate-y-0.5 transition-[border-color,box-shadow,transform] duration-300"
                data-testid={i < POS_PLATFORMS.length ? `pos-brand-${p.id}` : undefined}
              >
                <PosLogo p={p} />
                <span className="text-[13.5px] font-semibold text-neutral-800 whitespace-nowrap">{p.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------------------- Custom / tailored service box */}
      <section className="py-14 lg:py-20 bg-white" data-testid="custom-service-box">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-neutral-950 text-white p-8 sm:p-10 lg:p-14">
            <div
              aria-hidden="true"
              className="absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full bg-[#F77F11]/15 blur-[100px] pointer-events-none"
            />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <p className="inline-flex items-center gap-2 rounded-full border border-[#F77F11]/40 bg-[#F77F11]/10 px-4 py-1.5 text-[11px] font-semibold tracking-[0.16em] uppercase text-[#ffb374]">
                  <Wrench className="w-3.5 h-3.5" />
                  {isEs ? 'SERVICIO 100% PERSONALIZADO' : '100% TAILORED SERVICE'}
                </p>
                <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                  {isEs ? '¿Tu caso no encaja en ninguna caja?' : 'Doesn’t fit in any box?'}{' '}
                  <span className="bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">
                    {isEs ? 'Lo construimos a tu medida.' : 'We build it around you.'}
                  </span>
                </h2>
                <p className="mt-4 text-neutral-300 leading-relaxed text-[15.5px] max-w-xl">
                  {isEs
                    ? 'Cada restaurante es un mundo: sistemas heredados, múltiples locales, hardware antiguo, integraciones raras. Cuéntanos tu caso y diseñamos un plan de IT y POS exclusivo para tu operación — sin plantillas, sin paquetes forzados.'
                    : 'Every restaurant is different: legacy systems, multiple locations, aging hardware, unusual integrations. Tell us your case and we design an IT & POS plan exclusive to your operation — no templates, no forced bundles.'}
                </p>
                <ul className="mt-6 space-y-2.5">
                  {(isEs
                    ? [
                        'Diagnóstico gratuito de tu stack tecnológico actual.',
                        'Plan a medida con precios transparentes, sin sorpresas.',
                        'Migraciones de POS sin cerrar ni un solo día.',
                        'Soporte en español e inglés, 24/7.',
                      ]
                    : [
                        'Free diagnosis of your current technology stack.',
                        'Tailored plan with transparent pricing, no surprises.',
                        'POS migrations without closing a single day.',
                        'Support in English and Spanish, 24/7.',
                      ]
                  ).map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-neutral-300 text-[14.5px]">
                      <Check className="w-[18px] h-[18px] text-[#F77F11] shrink-0 mt-0.5" />
                      <span className="min-w-0">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-2xl bg-neutral-900/80 border border-neutral-800 p-6 sm:p-8">
                  <p className="text-sm font-semibold text-[#ffb374] uppercase tracking-wider">
                    {isEs ? 'Empieza aquí' : 'Start here'}
                  </p>
                  <p className="mt-3 text-neutral-300 text-[14.5px] leading-relaxed">
                    {isEs
                      ? 'Describe tu situación en 2 minutos. Rachel, nuestra concierge, te responde con próximos pasos claros — hoy mismo.'
                      : 'Describe your situation in 2 minutes. Rachel, our concierge, replies with clear next steps — today.'}
                  </p>
                  <div className="mt-6 flex flex-col gap-3">
                    <Button
                      asChild
                      className="bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 rounded-full text-[15px] font-semibold w-full"
                    >
                      <Link to="/#audit" data-testid="custom-service-audit-cta" className="group/audit">
                        <span className="inline-grid text-center">
                          <span className="col-start-1 row-start-1 transition-all duration-200 group-hover/audit:opacity-0 group-hover/audit:-translate-y-1.5">
                            {isEs ? 'Pedir mi plan a medida' : 'Get my tailored plan'}
                          </span>
                          <span className="col-start-1 row-start-1 opacity-0 translate-y-1.5 transition-all duration-200 group-hover/audit:opacity-100 group-hover/audit:translate-y-0 font-bold tracking-wide">
                            {isEs ? '¡HAZLO YA!' : 'DO IT NOW'}
                          </span>
                        </span>
                        <ArrowRight className="w-4 h-4 ml-1.5 transition-transform duration-200 group-hover/audit:translate-x-1" />
                      </Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="border-neutral-700 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 rounded-full text-[15px] font-semibold w-full"
                    >
                      <Link to="/contact" data-testid="custom-service-contact-cta">
                        <MessageCircle className="w-4 h-4 mr-1.5" />
                        {isEs ? 'Hablar con Rachel ahora' : 'Talk to Rachel now'}
                      </Link>
                    </Button>
                  </div>
                  <p className="mt-4 text-[12px] text-neutral-500 text-center">
                    {isEs ? 'Sin compromiso · Respuesta hoy mismo' : 'No commitment · Same-day response'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
